// maths.portll.net — printable recording sheet.
//
// A black-on-white, A4-friendly recording grid derived from a task's datasetSpec and
// printInsert template id. It is the OFFLINE half of a data task: the family collects
// on paper, then types the totals into DatasetEntry. Deliberately colour-free and
// ink-light so it prints cleanly on a home printer. Uses the .print-sheet / .no-print
// conventions from global.css so page chrome drops away under @media print.
//
// Nothing here is interactive or stateful — the owning page adds the "Print" button.

import { injectStyles } from "./ui.jsx";

// Map an authored printInsert template id (or fall back to the dataset kind) onto a
// grid renderer. New template aliases can be added here without touching content.
const TEMPLATE_ALIASES = {
  tally_sheet: "categorical_tally",
  tally_grid: "categorical_tally",
  category_tally: "categorical_tally",
  trial_boxes: "trial_outcomes",
  round_boxes: "trial_outcomes",
  chance_grid: "trial_outcomes",
  number_list: "numerical_list",
  value_list: "numerical_list",
  measure_list: "numerical_list",
  two_columns: "two_sample",
  compare_columns: "two_sample",
  dot_grid: "bivariate_pairs",
  coordinate_grid: "bivariate_pairs",
  scatter_grid: "bivariate_pairs",
};

function resolveTemplate(task) {
  const spec = task && task.datasetSpec;
  const fromId = task && task.printInsert ? TEMPLATE_ALIASES[task.printInsert] : null;
  return fromId || (spec && spec.kind) || "notes";
}

export default function PrintInsert({ task }) {
  if (!task) return null;
  const spec = task.datasetSpec || {};
  const template = resolveTemplate(task);

  let grid;
  switch (template) {
    case "categorical_tally":
      grid = <TallyGrid spec={spec} />;
      break;
    case "trial_outcomes":
      grid = <RoundBoxes spec={spec} />;
      break;
    case "numerical_list":
      grid = <NumberedList spec={spec} />;
      break;
    case "two_sample":
      grid = <TwoColumns spec={spec} />;
      break;
    case "bivariate_pairs":
      grid = <DotGrid spec={spec} />;
      break;
    default:
      grid = <NotesArea />;
  }

  const instruction =
    task.kidPromptCard ||
    task.hook ||
    "Collect your data below, then type the totals into the app together.";

  return (
    <section className="print-sheet pi-sheet" aria-label="Recording sheet">
      <header className="pi-head">
        <div className="pi-head__title">
          <h2 className="pi-title">{task.title || "Recording sheet"}</h2>
          <p className="pi-instruction">{instruction}</p>
        </div>
        <dl className="pi-fields">
          <div>
            <dt>Name</dt>
            <dd className="pi-blank" />
          </div>
          <div>
            <dt>Date</dt>
            <dd className="pi-blank" />
          </div>
        </dl>
      </header>

      <div className="pi-grid">{grid}</div>

      <footer className="pi-foot">
        <span>maths.portll.net · print at 100%</span>
        {task.safetyNote ? <span className="pi-safety">Safety: {task.safetyNote}</span> : null}
      </footer>
    </section>
  );
}

// ── categorical_tally → labelled rows with a wide tally box + running total ────
function TallyGrid({ spec }) {
  const suggested = Array.isArray(spec.suggestedCategories) ? spec.suggestedCategories : [];
  const kidDefined = spec.categories === "kid_defined" || suggested.length === 0;
  // Named rows plus a couple of blanks for surprises; all blanks when kid-defined.
  const rows = kidDefined
    ? Array.from({ length: 6 }, () => "")
    : [...suggested, "", ""];
  const unit = spec.unitLabel ? ` (${spec.unitLabel})` : "";
  return (
    <table className="pi-table pi-tally">
      <thead>
        <tr>
          <th className="pi-tally__cat">Thing to count{unit}</th>
          <th className="pi-tally__marks">Tally marks</th>
          <th className="pi-tally__total">Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((label, i) => (
          <tr key={i}>
            <td className="pi-tally__cat">{label || <span className="pi-writeline" />}</td>
            <td className="pi-tally__marks" />
            <td className="pi-tally__total" />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── trial_outcomes → numbered round boxes + an outcome tally summary ───────────
function RoundBoxes({ spec }) {
  const outcomes = Array.isArray(spec.outcomes) ? spec.outcomes : [];
  const count = Number.isFinite(spec.trials) && spec.trials > 0
    ? Math.min(60, spec.trials)
    : Number.isFinite(spec.maxTotal) && spec.maxTotal > 0
    ? Math.min(60, spec.maxTotal)
    : 30;
  const boxes = Array.from({ length: count }, (_, i) => i + 1);
  return (
    <div className="pi-trials">
      <p className="pi-sub">Write what happened in each round:</p>
      <div className="pi-boxes">
        {boxes.map((n) => (
          <div className="pi-box" key={n}>
            <span className="pi-box__n">{n}</span>
          </div>
        ))}
      </div>
      <table className="pi-table pi-summary">
        <thead>
          <tr>
            <th>Outcome</th>
            <th>Tally</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {(outcomes.length ? outcomes : [{ label: "" }, { label: "" }, { label: "" }]).map(
            (o, i) => (
              <tr key={i}>
                <td>{o.label || <span className="pi-writeline" />}</td>
                <td />
                <td />
              </tr>
            )
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── numerical_list → numbered blank lines with the unit repeated ───────────────
function NumberedList({ spec }) {
  const unit = spec.unitLabel || "";
  const n =
    Number.isFinite(spec.maxTotal) && spec.maxTotal > 0 ? Math.min(30, spec.maxTotal) : 16;
  const lines = Array.from({ length: n }, (_, i) => i + 1);
  return (
    <div className="pi-numlist">
      <p className="pi-sub">Write one measurement per line{unit ? ` (in ${unit})` : ""}:</p>
      <ol className="pi-lines">
        {lines.map((i) => (
          <li key={i}>
            <span className="pi-writeline" />
            {unit ? <span className="pi-unit">{unit}</span> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── two_sample → two labelled columns of numbered blank lines ──────────────────
function TwoColumns({ spec }) {
  const a = spec.groupALabel || "Group A";
  const b = spec.groupBLabel || "Group B";
  const lines = Array.from({ length: 10 }, (_, i) => i + 1);
  return (
    <div className="pi-twocol">
      {[a, b].map((label, gi) => (
        <div className="pi-col" key={gi}>
          <h3 className="pi-col__title">{label}</h3>
          <ol className="pi-lines">
            {lines.map((i) => (
              <li key={i}>
                <span className="pi-writeline" />
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

// ── bivariate_pairs → a coordinate dot grid + a paired (x,y) table ─────────────
function DotGrid({ spec }) {
  const x = spec.xLabel || "x";
  const y = spec.yLabel || "y";
  const size = 10; // 10 × 10 lattice
  const step = 26;
  const pad = 16;
  const dim = pad * 2 + step * size;
  const dots = [];
  for (let r = 0; r <= size; r++) {
    for (let c = 0; c <= size; c++) {
      dots.push(<circle key={`${r}-${c}`} cx={pad + c * step} cy={pad + r * step} r={1.3} />);
    }
  }
  const rows = Array.from({ length: 8 }, (_, i) => i + 1);
  return (
    <div className="pi-bivar">
      <p className="pi-sub">
        Plot each pair, then list them: across = {x}, up = {y}.
      </p>
      <div className="pi-bivar__body">
        <svg className="pi-dots" viewBox={`0 0 ${dim} ${dim}`} role="img" aria-label="Coordinate grid">
          <line x1={pad} y1={pad} x2={pad} y2={dim - pad} className="pi-axis" />
          <line x1={pad} y1={dim - pad} x2={dim - pad} y2={dim - pad} className="pi-axis" />
          <g className="pi-dotset">{dots}</g>
        </svg>
        <table className="pi-table pi-pairs">
          <thead>
            <tr>
              <th>{x}</th>
              <th>{y}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i}>
                <td />
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── fallback → a generic lined notes area ──────────────────────────────────────
function NotesArea() {
  const lines = Array.from({ length: 14 }, (_, i) => i);
  return (
    <div className="pi-notes">
      <p className="pi-sub">Record what you notice:</p>
      {lines.map((i) => (
        <div key={i} className="pi-noteline" />
      ))}
    </div>
  );
}

const PI_CSS = `
.pi-sheet{background:#fff;color:#000;max-width:820px;margin:0 auto;padding:24px 28px;
  border:1px solid #d9d9d9;border-radius:8px;font-family:var(--font);}
.pi-head{display:flex;flex-wrap:wrap;gap:12px 20px;align-items:flex-start;justify-content:space-between;
  border-bottom:2px solid #000;padding-bottom:12px;margin-bottom:16px;}
.pi-head__title{flex:1 1 16rem;min-width:0;}
.pi-title{margin:0;color:#000;font-size:1.35rem;}
.pi-instruction{margin:.35rem 0 0;font-size:.95rem;}
.pi-fields{display:flex;gap:16px;margin:0;}
.pi-fields div{display:flex;flex-direction:column;gap:4px;}
.pi-fields dt{font-size:.72rem;text-transform:uppercase;letter-spacing:.05em;}
.pi-fields dd{margin:0;}
.pi-blank{width:130px;border-bottom:1.5px solid #000;height:20px;}

.pi-sub{margin:0 0 10px;font-size:.95rem;}
.pi-writeline{display:inline-block;min-width:60px;flex:1 1 auto;border-bottom:1.5px solid #000;height:1.1em;vertical-align:bottom;}

.pi-table{width:100%;border-collapse:collapse;}
.pi-table th,.pi-table td{border:1.5px solid #000;padding:8px 10px;text-align:left;vertical-align:middle;}
.pi-table th{font-size:.8rem;text-transform:uppercase;letter-spacing:.03em;background:#f2f2f2;}
.pi-tally__cat{width:34%;}
.pi-tally__marks{width:48%;}
.pi-tally__total{width:18%;}
.pi-tally tbody td{height:44px;}

.pi-boxes{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-bottom:18px;}
.pi-box{position:relative;aspect-ratio:1/1;border:1.5px solid #000;border-radius:4px;min-height:44px;}
.pi-box__n{position:absolute;top:3px;left:5px;font-size:.7rem;color:#555;}
.pi-summary{margin-top:6px;}
.pi-summary td{height:40px;}

.pi-lines{margin:0;padding-left:1.6em;}
.pi-lines li{display:flex;align-items:flex-end;gap:8px;height:38px;}
.pi-lines li .pi-writeline{flex:1 1 auto;}
.pi-unit{font-size:.85rem;color:#333;}

.pi-numlist .pi-lines{columns:2;column-gap:40px;}
.pi-numlist .pi-lines li{break-inside:avoid;}

.pi-twocol{display:grid;grid-template-columns:1fr 1fr;gap:24px;}
.pi-col__title{margin:0 0 8px;font-size:1rem;border-bottom:1.5px solid #000;padding-bottom:4px;}

.pi-bivar__body{display:flex;flex-wrap:wrap;gap:20px;align-items:flex-start;}
.pi-dots{flex:0 0 auto;width:300px;max-width:100%;height:auto;}
.pi-dots .pi-dotset circle{fill:#777;}
.pi-dots .pi-axis{stroke:#000;stroke-width:1.5;}
.pi-pairs{flex:1 1 160px;min-width:150px;}
.pi-pairs td{height:34px;}

.pi-notes{display:flex;flex-direction:column;gap:0;}
.pi-noteline{height:38px;border-bottom:1.5px solid #000;}

.pi-foot{display:flex;flex-wrap:wrap;gap:6px 16px;justify-content:space-between;margin-top:18px;
  padding-top:10px;border-top:1px solid #999;font-size:.72rem;color:#333;}
.pi-safety{font-weight:600;}

@media print{
  .pi-sheet{border:none;border-radius:0;max-width:none;padding:0;}
  .pi-table th{background:#eee !important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
}
`;

injectStyles("maths-printinsert-styles", PI_CSS);
